"use client";

import { useEffect, useMemo, useState } from "react";
import FeatureBarChart from "@/components/MicrobeBarChart";

type MicrobeWeight = {
  name: string;
  weight: number;
};

type DiseaseResult = {
  job_id: string;
  query_sample: string;
  prediction: string;
  probability: number;
  top_microbes: MicrobeWeight[];
  matched_samples: number;
  generated_at: string;
};

export default function DiseaseAIPage() {
  const API = process.env.NEXT_PUBLIC_API_URL;

if (!API) {
  throw new Error("NEXT_PUBLIC_API_URL is not defined");
}

const BASE_API = API.replace(/\/$/, "");


  const [microbeFile, setMicrobeFile] = useState<File | null>(null);
  const [metaFile, setMetaFile] = useState<File | null>(null);

  const [jobId, setJobId] = useState<string | null>(null);
  const [status, setStatus] = useState<string | null>(null);

  const [result, setResult] = useState<DiseaseResult | null>(null);
  const [error, setError] = useState<string | null>(null);

  const [loadingSubmit, setLoadingSubmit] = useState(false);

  /* ================= Submit job (UNCHANGED) ================= */
  const run = async () => {
    if (!microbeFile || !metaFile) {
      setError("Both Microbial TSV and Metadata TSV are required.");
      return;
    }

    setError(null);
    setResult(null);
    setJobId(null);
    setStatus(null);
    setLoadingSubmit(true);

    try {
      const formData = new FormData();
      formData.append("microbe_file", microbeFile);
      formData.append("meta_file", metaFile);

      const res = await fetch(`${API}/api/disease/run`, {
        method: "POST",
        body: formData,
      });

      if (!res.ok) {
        const t = await res.text();
        throw new Error(t || "Failed to submit job");
      }

      const data = await res.json();
      setJobId(data.job_id);
      setStatus("queued");
    } catch (e: any) {
      setError(e?.message || "Failed to submit Disease AI job");
    } finally {
      setLoadingSubmit(false);
    }
  };

  /* ================= Poll status (UNCHANGED) ================= */
  useEffect(() => {
    if (!jobId) return;

    let timer: any;
    let cancelled = false;

    const poll = async () => {
      try {
        const s = await fetch(`${API}/api/disease/status/${jobId}`);
        const statusData = await s.json();

        if (statusData.error) {
          setError(statusData.error);
          setStatus("failed");
          return;
        }

        setStatus(statusData.status);

        if (statusData.status === "failed") {
          setError(statusData.error || "Disease AI pipeline failed");
          return;
        }

        if (statusData.status !== "completed") {
          timer = setTimeout(poll, 1500);
          return;
        }

        const r = await fetch(`${API}/api/disease/result/${jobId}`);
        const resultData = await r.json();

        if (!cancelled) setResult(resultData);
      } catch {
        if (!cancelled) setError("Failed to fetch Disease AI result");
      }
    };

    poll();

    return () => {
      cancelled = true;
      if (timer) clearTimeout(timer);
    };
  }, [jobId, API]);

  /* ================= UI ================= */
  return (
    <div
      className="
        min-h-screen
        bg-gradient-to-br from-black via-indigo-900 to-purple-900
        px-6 pt-20 pb-20
        text-white
      "
    >
      {/* ================= Upload Card ================= */}
      <div
        className="
          w-full max-w-xl mx-auto
          bg-white/10 backdrop-blur-xl
          border border-white/20
          rounded-2xl p-8
          shadow-2xl space-y-6
        "
      >
        <div className="text-center border-b border-white/10 pb-4">
          <h1 className="text-2xl font-extrabold">
            Disease Association (AI)
          </h1>
          <p className="mt-2 text-sm text-indigo-200 leading-relaxed">
            Predict disease state using microbial abundance
            <br />
            and clinical metadata with explainable AI
          </p>
        </div>

        {/* Microbial file */}
        <input
          type="file"
          accept=".tsv,.csv"
          onChange={(e) => setMicrobeFile(e.target.files?.[0] || null)}
          className="
            block w-full text-sm
            file:mr-4 file:py-2 file:px-4
            file:rounded-xl file:border-0
            file:text-sm file:font-semibold
            file:bg-indigo-600 file:text-white
            hover:file:bg-indigo-500
          "
        />

        {/* Metadata file */}
        <input
          type="file"
          accept=".tsv,.csv"
          onChange={(e) => setMetaFile(e.target.files?.[0] || null)}
          className="
            block w-full text-sm
            file:mr-4 file:py-2 file:px-4
            file:rounded-xl file:border-0
            file:text-sm file:font-semibold
            file:bg-purple-600 file:text-white
            hover:file:bg-purple-500
          "
        />

        {/* Run */}
        <button
          onClick={run}
          disabled={loadingSubmit || !microbeFile || !metaFile}
          className="
            w-full py-3 rounded-xl font-semibold
            bg-gradient-to-r from-rose-500 to-fuchsia-600
            hover:scale-[1.02] transition
            disabled:opacity-40
          "
        >
          {loadingSubmit ? "Submitting…" : "Run Disease AI Analysis"}
        </button>

        {jobId && (
          <p className="text-xs text-indigo-200">
            Job ID: <span className="font-mono">{jobId}</span>
          </p>
        )}

        {status && (
          <p className="text-sm text-indigo-300">
            Status: <strong>{status}</strong>
          </p>
        )}

        {error && (
          <p className="text-sm text-red-400 text-center">
            {error}
          </p>
        )}
      </div>

      {/* ================= Results ================= */}
      {result && (
        <div className="mt-20 max-w-6xl mx-auto space-y-10">
          {/* Prediction summary */}
          <div
            className="
              bg-white/10 backdrop-blur-xl
              border border-white/20
              rounded-2xl p-6
              shadow-xl
              flex flex-col md:flex-row
              justify-between gap-6
            "
          >
            <div>
              <h2 className="text-xl font-bold mb-2">AI Prediction</h2>
              <p className="text-lg">
                Prediction:{" "}
                <span className="font-extrabold text-rose-300">
                  {result.prediction || "N/A"}
                </span>
              </p>
              <p className="text-sm text-indigo-200 mt-1">
                Confidence:{" "}
                <strong>
                  {isNaN(result.probability)
                    ? "N/A"
                    : `${(result.probability * 100).toFixed(1)}%`}
                </strong>
              </p>
            </div>

            <div className="text-sm text-indigo-200">
              Samples used:{" "}
              <strong>{result.matched_samples}</strong>
            </div>
          </div>

          {/* Explainable AI */}
          <div
            className="
              bg-white/10 backdrop-blur-xl
              border border-white/20
              rounded-2xl p-6
              shadow-xl
            "
          >
            <h3 className="font-semibold mb-4 text-indigo-200">
              Explainable AI – Feature Importance
            </h3>

            <FeatureBarChart data={result.top_microbes || []} />
          </div>

          {/* Table */}
          <div
            className="
              bg-white/10 backdrop-blur-xl
              border border-white/20
              rounded-2xl p-6
              shadow-xl
            "
          >
            <h3 className="font-semibold mb-3 text-indigo-200">
              Top Contributing Microbes
            </h3>

            {result.top_microbes.length === 0 ? (
              <p className="text-sm text-indigo-300">
                No significant microbial contributors detected.
              </p>
            ) : (
              <table className="w-full text-sm">
                <thead className="bg-black/40">
                  <tr>
                    <th className="px-3 py-2 text-left">Microbe</th>
                    <th className="px-3 py-2 text-left">Weight</th>
                  </tr>
                </thead>
                <tbody>
                  {result.top_microbes.map((m, i) => (
                    <tr
                      key={i}
                      className="border-t border-white/10 hover:bg-white/5"
                    >
                      <td className="px-3 py-2">{m.name}</td>
                      <td
                        className={`px-3 py-2 font-semibold ${
                          m.weight > 0
                            ? "text-rose-300"
                            : "text-emerald-300"
                        }`}
                      >
                        {m.weight.toFixed(4)}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </div>

          <a
            href={`${API}/api/disease/result/${result.job_id}`}
            target="_blank"
            rel="noreferrer"
            className="
              inline-block
              bg-indigo-600 hover:bg-indigo-500
              text-white px-5 py-2 rounded-xl font-semibold
            "
          >
            Download JSON Result
          </a>
        </div>
      )}
    </div>
  );
}
